import { useCallback, useMemo } from 'react';

import type { CodeString } from '@/consts/codes';
import { useAddFavorite, useFavorites, useRemoveFavorite } from '@/modules/MD/api/settings/hooks';

interface FavoritesStore {
  favoriteCodes: Set<string>;
  favoritesLoading: boolean;
  isTogglingFavorite: boolean;
  isFavorite: (code: CodeString | undefined) => boolean;
  toggleFavorite: (code: CodeString | undefined) => Promise<void>;
}

export const useFavoritesStore = (): FavoritesStore => {
  const { data: favoritesArray, isLoading: favoritesLoading } = useFavorites();
  const addFavorite = useAddFavorite();
  const removeFavorite = useRemoveFavorite();

  const favoriteCodes = useMemo(() => new Set(favoritesArray ?? []), [favoritesArray]);
  const isTogglingFavorite = addFavorite.isPending || removeFavorite.isPending;

  const isFavorite = useCallback(
    (code: CodeString | undefined): boolean => {
      if (!code) return false;
      return favoriteCodes.has(code);
    },
    [favoriteCodes]
  );

  const toggleFavorite = useCallback(
    async (code: CodeString | undefined): Promise<void> => {
      if (!code) return;

      try {
        if (favoriteCodes.has(code)) {
          await removeFavorite.mutateAsync(code);
        } else {
          await addFavorite.mutateAsync(code);
        }
      } catch (error) {
        console.error('Error toggling favorite:', error);
      }
    },
    [favoriteCodes, addFavorite, removeFavorite]
  );

  return {
    favoriteCodes,
    favoritesLoading,
    isTogglingFavorite,
    isFavorite,
    toggleFavorite
  };
};
